import "./index.css";
import "./i18n";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { GarageGate } from "./components/app/garages";
import { TooltipProvider } from "./components/ui/tooltip";
import { AccountApp } from "./features/account/account-app";
import { AdminApp } from "./features/admin/admin-app";
import { setAuthMode } from "./lib/account";
import { canonicalizeUrl, garage, section } from "./lib/garage";
import { createQueryClient } from "./lib/query";
import { ThemeProvider } from "./lib/theme";
import { TRPCProvider, trpcClient } from "./lib/trpc";
import { routeTree } from "./routeTree.gen";

const queryClient = createQueryClient();

// The router runs below the garage's address: /my-garage/inventory is its /inventory.
const router = createRouter({
  routeTree,
  basepath: `/${garage ?? ""}`,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

/** /account/… and /admin/… are the app's own pages; anything else is a garage (or "/"). */
function App() {
  if (section === "account") return <AccountApp />;
  if (section === "admin") return <AdminApp />;
  return garage ? <RouterProvider router={router} /> : <GarageGate />;
}

async function start() {
  if (!canonicalizeUrl()) return;
  // Whether people sign in here decides what the pages show (ADR 0011).
  const info = await trpcClient.system.info.query().catch(() => null);
  setAuthMode(info?.auth ?? "none");
  createRoot(document.getElementById("root") as HTMLElement).render(
    <StrictMode>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
            <TooltipProvider delayDuration={300}>
              <App />
            </TooltipProvider>
          </TRPCProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </StrictMode>,
  );
}

void start();
